// Inbox-tab van Organize: panels (triage-bron) + dashed dropzones.
import { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import TaskList from '@/features/tasks/components/lists/TaskListBase';
import Panel from '@/components/ui/Panel';
import OrganizeSplitPane from '@/features/organize/components/OrganizeSplitPane';
import InboxDropzones from '@/features/organize/components/InboxDropzones';
import InboxDragOverlay from '@/features/organize/components/InboxDragOverlay';
import { useInboxViewController } from '@/features/organize/hooks/useInboxViewController';

const InboxView = ({ onContextPanelOpenChange }) => {
  const {
    sensors,
    activeTask,
    groups,
    safeOpenGroup,
    activeGroup,
    detailZones,
    tagAddConfig,
    highlightZoneId,
    detailEmptyMessage,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handlePanelToggle,
    handleZoneKeyboardAssign,
  } = useInboxViewController(onContextPanelOpenChange);

  const [selectedTaskIds, setSelectedTaskIds] = useState([]);

  useEffect(() => {
    setSelectedTaskIds([]);
  }, [safeOpenGroup]);

  const selectedTaskId =
    selectedTaskIds.length === 1 ? selectedTaskIds[0] : null;

  const handleAssignToZone = useCallback(
    async (zoneId) => {
      if (!selectedTaskId) return;
      const tasks = activeGroup?.tasks ?? [];
      const taskIndex = tasks.findIndex((task) => task.id === selectedTaskId);
      await handleZoneKeyboardAssign(zoneId, selectedTaskId);
      const remaining = tasks.filter((task) => task.id !== selectedTaskId);
      const nextTask =
        remaining[taskIndex] ?? remaining[taskIndex - 1] ?? null;
      setSelectedTaskIds(nextTask ? [nextTask.id] : []);
    },
    [activeGroup?.tasks, handleZoneKeyboardAssign, selectedTaskId],
  );

  const master = (
    <Box
      sx={{
        p: 1,
        overflowY: 'auto',
        ...(activeTask && { '& .task-item': { pointerEvents: 'none' } }),
      }}
    >
      {groups.map((group, i) => (
        <Panel
          key={group.key}
          flat
          title={group.label}
          count={group.count}
          open={safeOpenGroup === i}
          onToggle={handlePanelToggle(i)}
          testId={`inbox-panel-${group.key}`}
        >
          {group.tasks.length > 0 && safeOpenGroup === i ? (
            <TaskList
              id={group.listId}
              tasks={group.tasks}
              view="organize"
              menuSurface="organize-inbox"
              isSortable={false}
              dragEnabled={true}
              indentationType="none"
              showBreadcrumbs={false}
              disableStructuralNav={true}
              selectedTaskIds={selectedTaskIds}
              onSelectionChange={setSelectedTaskIds}
              listTabIndex={group.tasks.length > 0 ? 0 : -1}
              itemTabIndex={-1}
            />
          ) : null}
        </Panel>
      ))}
    </Box>
  );

  const detail = (
    <InboxDropzones
      zones={detailZones}
      emptyMessage={detailEmptyMessage}
      addPlaceholder={tagAddConfig?.placeholder}
      onAddTag={tagAddConfig?.onAdd}
      addInputTestId={tagAddConfig?.testId}
      highlightZoneId={highlightZoneId}
      showDropHint={(activeGroup?.tasks?.length ?? 0) > 0}
      selectedTaskId={selectedTaskId}
      onAssignToZone={handleAssignToZone}
    />
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <OrganizeSplitPane
        master={master}
        detail={detail}
        detailVisible={true}
        onCloseDetail={() => {}}
      />
      <DragOverlay dropAnimation={null}>
        <InboxDragOverlay activeTask={activeTask} />
      </DragOverlay>
    </DndContext>
  );
};

export default InboxView;
